import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { posVisitaConfigService, posVisitaRespostasService } from '../../services/visitasConfigService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface VisitaPendente {
  id: string;
  codigo: string;
  assunto: string;
  data_visita: string;
  horario_inicio: string;
  horario_fim: string;
  comprador?: { id: string; nome: string };
}

interface Pergunta {
  id: string;
  pergunta: string;
  tipo: 'estrelas' | 'sim_nao' | 'texto';
  ordem: number;
}

const PosVisitaModal: React.FC = () => {
  const { user } = useAuth();
  const [visitasPendentes, setVisitasPendentes] = useState<VisitaPendente[]>([]);
  const [visitaAtual, setVisitaAtual] = useState<VisitaPendente | null>(null);
  const [perguntas, setPerguntas] = useState<Pergunta[]>([]);
  const [respostas, setRespostas] = useState<Record<string, any>>({});
  const [enviando, setEnviando] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [fechado, setFechado] = useState(false);

  useEffect(() => {
    if (!user || user.perfil === 'admin' || user.perfil === 'comprador' || user.perfil === 'recepcao') {
      setCarregando(false);
      return;
    }
    verificarPendentes();
  }, [user]);

  const verificarPendentes = async () => {
    try {
      setCarregando(true);
      const [pendResp, configResp] = await Promise.all([
        posVisitaRespostasService.pendentes(),
        posVisitaConfigService.listar()
      ]);

      const pendentes = pendResp?.data || [];
      const configPerguntas = configResp?.data || [];

      if (pendentes.length > 0 && configPerguntas.length > 0) {
        setVisitasPendentes(pendentes);
        setVisitaAtual(pendentes[0]);
        setPerguntas(configPerguntas);
      }
    } catch (error) {
      console.error('Erro ao verificar visitas pendentes:', error);
    } finally {
      setCarregando(false);
    }
  };

  const handleResposta = (perguntaId: string, valor: any) => {
    setRespostas(prev => ({ ...prev, [perguntaId]: valor }));
  };

  const handleSubmit = async () => {
    if (!visitaAtual) return;

    const todasRespondidas = perguntas.every(p => {
      const r = respostas[p.id];
      return r !== undefined && r !== '' && r !== null;
    });

    if (!todasRespondidas) {
      alert('Por favor, responda todas as perguntas antes de enviar.');
      return;
    }

    setEnviando(true);
    try {
      const respostasArray = perguntas.map(p => ({
        pergunta_id: p.id,
        pergunta: p.pergunta,
        tipo: p.tipo,
        resposta: respostas[p.id]
      }));

      await posVisitaRespostasService.salvar(visitaAtual.id, respostasArray);

      const restantes = visitasPendentes.filter(v => v.id !== visitaAtual.id);
      if (restantes.length > 0) {
        setVisitasPendentes(restantes);
        setVisitaAtual(restantes[0]);
        setRespostas({});
      } else {
        setVisitaAtual(null);
        setVisitasPendentes([]);
        setFechado(true);
      }
    } catch (error) {
      console.error('Erro ao enviar respostas:', error);
      alert('Erro ao enviar respostas. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  const handlePular = () => {
    const restantes = visitasPendentes.filter(v => v.id !== visitaAtual?.id);
    if (restantes.length > 0) {
      setVisitasPendentes(restantes);
      setVisitaAtual(restantes[0]);
      setRespostas({});
    } else {
      setVisitaAtual(null);
      setVisitasPendentes([]);
      setFechado(true);
    }
  };

  const handleFechar = () => {
    setFechado(true);
    setVisitaAtual(null);
  };

  if (carregando || fechado || !visitaAtual || perguntas.length === 0) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-5 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl">
          <h2 className="text-lg font-bold text-white">📋 Questionário de Pós Visita</h2>
          <p className="text-sm text-blue-100 mt-1">
            Sua opinião é de suma importância para melhorarmos nosso atendimento
          </p>
        </div>

        {/* Info da visita */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900">{visitaAtual.assunto}</p>
              <p className="text-sm text-gray-600">
                Comprador: <strong>{visitaAtual.comprador?.nome || 'N/A'}</strong>
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {format(new Date(visitaAtual.data_visita + 'T12:00:00'), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                {' '} das {visitaAtual.horario_inicio} às {visitaAtual.horario_fim}
              </p>
            </div>
            {visitasPendentes.length > 1 && (
              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                {visitasPendentes.length} pendentes
              </span>
            )}
          </div>
        </div>

        {/* Perguntas */}
        <div className="p-5 space-y-5">
          {perguntas.map((p, idx) => (
            <div key={p.id}>
              <label className="block text-sm font-medium text-gray-800 mb-2">
                {idx + 1}. {p.pergunta}
              </label>

              {p.tipo === 'estrelas' && (
                <div className="flex gap-1">
                  {[0, 1, 2, 3, 4, 5].map(n => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => handleResposta(p.id, n)}
                      className={`text-2xl transition-transform hover:scale-110 ${
                        respostas[p.id] !== undefined && n <= respostas[p.id]
                          ? 'text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    >
                      ★
                    </button>
                  ))}
                  {respostas[p.id] !== undefined && (
                    <span className="ml-2 text-sm text-gray-600 self-center">
                      {respostas[p.id] === 0 ? 'Sem avaliação' : `${respostas[p.id]} estrela${respostas[p.id] > 1 ? 's' : ''}`}
                    </span>
                  )}
                </div>
              )}

              {p.tipo === 'sim_nao' && (
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => handleResposta(p.id, 'sim')}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                      respostas[p.id] === 'sim'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-green-50'
                    }`}
                  >
                    ✓ Sim
                  </button>
                  <button
                    type="button"
                    onClick={() => handleResposta(p.id, 'nao')}
                    className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                      respostas[p.id] === 'nao'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-red-50'
                    }`}
                  >
                    ✗ Não
                  </button>
                </div>
              )}

              {p.tipo === 'texto' && (
                <textarea
                  value={respostas[p.id] || ''}
                  onChange={e => handleResposta(p.id, e.target.value)}
                  rows={3}
                  placeholder="Digite sua resposta..."
                  className="w-full rounded-lg border-2 border-gray-300 focus:border-blue-500 px-3 py-2 text-gray-900 text-sm"
                />
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex gap-3 justify-end bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={handlePular}
            className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800"
          >
            Pular por agora
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={enviando}
            className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {enviando ? 'Enviando...' : 'Enviar Respostas'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PosVisitaModal;
